import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import { ViewIcon, DeleteIcon, InvoicesIcon } from '../components/Icons';
import './AdminCreditInvoices.css';

function AdminCreditInvoices() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotification();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
  const [filters, setFilters] = useState({
    status: '',
    order_id: '',
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-credit-invoices', filters, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit === 'all' ? '999999' : limit.toString(),
      });
      
      if (filters.status) params.append('status', filters.status);
      if (filters.order_id) params.append('order_id', filters.order_id);

      const res = await apiClient.get(`/admin/credit-invoices?${params.toString()}`);
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/admin/credit-invoices/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-credit-invoices']);
      success('Kreditinė sąskaita pašalinta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida šalinant kreditinę sąskaitą');
    },
  });

  const handleDelete = (id, creditNumber) => {
    setConfirmModal({
      isOpen: true,
      title: 'Pašalinti kreditinę sąskaitą',
      message: `Ar tikrai norite pašalinti kreditinę sąskaitą "${creditNumber}"?`,
      type: 'danger',
      confirmText: 'Pašalinti',
      cancelText: 'Atšaukti',
      onConfirm: () => {
        deleteMutation.mutate(id);
        setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
      },
      onCancel: () => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' })
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'draft': 'status-draft',
      'issued': 'status-paid',
      'cancelled': 'status-cancelled',
    };
    return classes[status] || '';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'draft': 'Juodraštis',
      'issued': 'Išrašyta',
      'cancelled': 'Atšaukta',
    };
    return labels[status] || status;
  };

  if (isLoading) {
    return <div className="admin-credit-invoices-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-credit-invoices-page container">
      <div className="credit-invoices-header">
        <div>
          <h1>
            <InvoicesIcon />
            Kreditinės sąskaitos
          </h1>
          <p className="header-subtitle">Kreditinės sąskaitos grąžinimams ir kompensacijoms</p>
        </div>
      </div>

      {/* Filtrai */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Statusas</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setPage(1);
              }}
              className="input"
            >
              <option value="">Visi</option>
              <option value="draft">Juodraštis</option>
              <option value="issued">Išrašyta</option>
              <option value="cancelled">Atšaukta</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Užsakymo ID</label>
            <input
              type="text"
              value={filters.order_id}
              onChange={(e) => {
                setFilters({ ...filters, order_id: e.target.value });
                setPage(1);
              }}
              className="input"
              placeholder="Ieškoti pagal užsakymo ID..."
            />
          </div>

          <div className="filter-actions">
            <button
              onClick={() => {
                setFilters({
                  status: '',
                  order_id: '',
                });
                setPage(1);
              }}
              className="btn btn-secondary btn-sm"
            >
              Išvalyti
            </button>
          </div>
        </div>
      </div>

      {/* Kreditinių sąskaitų sąrašas */}
      <div className="credit-invoices-table-wrapper">
        <table className="credit-invoices-table">
          <thead>
            <tr>
              <th>Kreditinės S-tos Nr.</th>
              <th>Originali S-ta</th>
              <th>Užsakymo Nr.</th>
              <th>Klientas</th>
              <th>Data</th>
              <th>Suma</th>
              <th>Statusas</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {data?.creditInvoices?.map((creditInvoice) => (
              <tr key={creditInvoice.id}>
                <td>
                  <Link to={`/admin/credit-invoices/${creditInvoice.id}`} className="credit-link">
                    {creditInvoice.credit_number}
                  </Link>
                </td>
                <td>
                  {creditInvoice.original_invoice_number ? (
                    <Link to={`/admin/invoices/${creditInvoice.original_invoice_id}`} className="invoice-link">
                      {creditInvoice.original_invoice_number}
                    </Link>
                  ) : '—'}
                </td>
                <td>
                  {creditInvoice.order_number ? (
                    <Link to={`/admin/orders/${creditInvoice.order_id}`} className="order-link">
                      {creditInvoice.order_number}
                    </Link>
                  ) : '—'}
                </td>
                <td>
                  {creditInvoice.customer_email || (
                    <span className="guest-customer">Svečias</span>
                  )}
                  {creditInvoice.customer_name && (
                    <div className="customer-name">
                      {creditInvoice.customer_name}
                    </div>
                  )}
                </td>
                <td>{formatDate(creditInvoice.issue_date)}</td>
                <td className="amount">-{parseFloat(creditInvoice.total).toFixed(2)} €</td>
                <td>
                  <span className={`status-badge ${getStatusBadgeClass(creditInvoice.status)}`}>
                    {getStatusLabel(creditInvoice.status)}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <Link
                      to={`/admin/credit-invoices/${creditInvoice.id}`}
                      className="btn-icon btn-icon-secondary"
                      title="Peržiūrėti"
                    >
                      <ViewIcon />
                    </Link>
                    {creditInvoice.status === 'draft' && (
                      <button
                        onClick={() => handleDelete(creditInvoice.id, creditInvoice.credit_number)}
                        className="btn-icon btn-icon-danger"
                        title="Pašalinti"
                      >
                        <DeleteIcon />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Puslapiavimas */}
      {data?.pagination && (
        <Pagination
          page={data.pagination.page}
          totalPages={data.pagination.pages}
          totalItems={data.pagination.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel}
      />
    </div>
  );
}

export default AdminCreditInvoices;
